import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import IncludedTiles from "@/components/IncludedTiles"
import LocationCards, { type LocationCard } from "@/components/LocationCards"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { trackConversion, visitSucceeded } from "@/lib/analytics"
import { FormError, useHoneypot } from "@/components/public-form"
import { HeroFilm, HeroFilmSlides, type HeroPhoto } from "@/components/gisela/HeroBand"
import { bandBg } from "@/components/gisela/TextBand"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import { absoluteUrl } from "@/lib/absolute-url"
import type { GiselaTherapist, PageMeta, PageProps } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-display">{message}</p>
}

interface Photo {
  id: number
  url: string | null
  /** 1200×630 JPEG for og:image — link-preview scrapers often refuse WebP. */
  social_url?: string | null
  alt_text: string | null
}

interface FeatureItem {
  icon: string
  title_en: string
  desc_en?: string
}

interface Props {
  sites: LocationCard[]
  /** Gisela, for the therapy card — nil until a therapist record exists. */
  therapist?: GiselaTherapist | null
  /** The hero's background photos, already cut to its display settings. */
  hero?: { slides: Photo[]; interval_seconds: number; film?: string | null }
  content: {
    hero: { heading: string; subheading: string; _hidden?: boolean }
    whats_included: { items?: FeatureItem[]; _hidden?: boolean }
    rates: { heading?: string; body?: string; table?: string; note?: string; _hidden?: boolean }
    community: { heading: string; body: string; _hidden?: boolean }
    /** Bold question line, then its answer — also the page's FAQPage JSON-LD. */
    faq?: { heading?: string; body?: string; _hidden?: boolean }
    /** The therapy card's background: a chosen photo under the navy film, or none for plain navy. */
    therapy_link?: { photo?: HeroPhoto | null; _hidden?: boolean }
  }
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

export default function RentARoom({
  sites,
  therapist,
  hero = { slides: [], interval_seconds: 6 },
  content,
  section_order,
  meta,
}: Props) {
  const title = meta?.title ?? "Therapy Room Hire in Prestwich, Manchester"
  const description =
    meta?.description ??
    "Rent a professional therapy room in Prestwich or Hebden Bridge. Free parking, sound masking, in-house booking and billing, and a welcoming Victorian building."

  const { props } = usePage<PageProps>()
  const { brand_urls } = props
  const sanctuary_locations = props.sanctuary_locations ?? []
  const t = props.translations.sanctuary_pages.rent
  const tc = props.translations.sanctuary_pages.common
  const errors = props.errors ?? {}
  const notice = props.flash.notice

  const [name, setName] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [phone, setPhone] = React.useState("")
  const [modality, setModality] = React.useState("")
  const [locationPref, setLocationPref] = React.useState("")
  const [preferredDays, setPreferredDays] = React.useState<string[]>([])
  const [notes, setNotes] = React.useState("")
  const [processing, setProcessing] = React.useState(false)
  const honeypot = useHoneypot("vr-company")

  function toggleDay(day: string) {
    setPreferredDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      "/viewing-requests",
      {
        viewing_request: {
          name,
          email,
          phone,
          modality,
          location_pref: locationPref,
          preferred_days: preferredDays,
          notes,
        },
        ...honeypot.payload,
      },
      {
        onSuccess: (page) => {
          if (visitSucceeded(page)) trackConversion("viewing_request")
        },
        onFinish: () => {
          setProcessing(false)
          // Inertia's fetch-based redirect drops the URL fragment, so restore it
          // to keep the confirmation/error state anchored to the form section.
          window.location.hash = "book-a-viewing"
        },
      }
    )
  }

  // Every band below the hero, named by its registry entry so the admin's
  // order applies; the cream/stone colours alternate over the ones shown.
  const bands = presentBands(
    [
      {
        id: "rent-a-room-locations",
        present: true,
        render: (i) => (
                <section id="locations" className={`${bandBg(i + 1)} py-16`}>
                  <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mb-10 text-center">
                      <h2>{t.locations_heading}</h2>
                      <p className="mt-3 text-ink-muted">{t.locations_sub}</p>
                    </div>
                    {/* One card per location, each to its own page — the rooms,
                        photos and directions live there. */}
                    <LocationCards sites={sites} />
                  </div>
                </section>
        ),
      },
      {
        id: "rent-a-room-whats-included",
        present: !sectionHidden(content.whats_included),
        render: (i) => (
                  <section id="whats-included" className={`relative overflow-hidden ${bandBg(i + 1)} py-16`}>
                    <BrandMotif variant="sprig" className="absolute -bottom-12 -right-8" />
                    <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                      <h2 className="text-center">{tc.whats_included}</h2>
                      <p className="mt-3 text-center text-ink-muted">
                        {t.whats_included_sub}
                      </p>

                      {/* The same icon tiles as the location pages. */}
                      <div className="mt-10">
                        <IncludedTiles items={content.whats_included.items ?? []} />
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: "rent-a-room-rates",
        // Nothing to say and no table to show means no band — the same
        // presence gate every other band on this page uses.
        present: !sectionHidden(content.rates) && !!(content.rates.body || content.rates.table),
        render: (i) => (
                  <section id="rates" className={`${bandBg(i + 1)} py-16`}>
                    <div className="mx-auto max-w-3xl px-6 lg:px-8">
                      <h2>{content.rates.heading}</h2>
                      {content.rates.body && (
                        <div className="mt-5 text-ink-muted [&_p]:mt-4 [&_p:first-child]:mt-0">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.rates.body}</ReactMarkdown>
                        </div>
                      )}
                      {/* The table is its own field, so this container wraps
                          exactly the table and can scroll it on a phone. */}
                      {content.rates.table && (
                        <div className="body-content mt-6 overflow-x-auto text-ink-body">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.rates.table}</ReactMarkdown>
                        </div>
                      )}
                      {content.rates.note && (
                        <div className="mt-5 text-sm text-ink-muted [&_p]:mt-3 [&_p:first-child]:mt-0">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.rates.note}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </section>
        ),
      },
      {
        id: "rent-a-room-community",
        present: !sectionHidden(content.community),
        render: (i) => (
                  <section className={`relative overflow-hidden ${bandBg(i + 1)} py-16`}>
                    <BrandMotif variant="disc" className="absolute -top-28 -right-36" />
                    <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
                      <h2>{content.community.heading}</h2>
                      <div className="mt-5 text-ink-muted [&_p]:mt-4 [&_p:first-child]:mt-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.community.body}</ReactMarkdown>
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: "rent-a-room-faq",
        present: !!content.faq && !sectionHidden(content.faq) && !!content.faq.body,
        render: (i) => (
                  <section id="faq" className={`${bandBg(i + 1)} py-16`}>
                    <div className="mx-auto max-w-3xl px-6 lg:px-8">
                      <h2>{content.faq?.heading || t.faq_heading}</h2>
                      <div className="body-content mt-6 text-ink-body">
                        {/* A bold line is a question (FaqMarkdown's convention),
                            so it takes a line of its own above its answer. */}
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{ strong: ({ children }) => <strong className="mb-1 block">{children}</strong> }}
                        >
                          {content.faq?.body ?? ""}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: "rent-a-room-viewing-form",
        present: true,
        render: (i) => (
                <section id="book-a-viewing" className={`${bandBg(i + 1)} py-16`}>
                  <div className="mx-auto max-w-2xl px-6 lg:px-8">
                    <h2>{t.book_viewing_heading}</h2>

                    {notice ? (
                      <div className="mt-8 rounded-2xl bg-white p-8 text-center ring-1 ring-hairline">
                        <h3>{t.thank_you}</h3>
                        <p className="mt-3 text-ink-muted">{notice}</p>
                      </div>
                    ) : (
                      <>
                        <p className="mt-3 text-ink-muted">
                          {t.form_intro}
                        </p>

                        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                          <FormError message={errors.base} />
                          {honeypot.field}
                          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                              <label htmlFor="vr-name">{t.name_label}</label>
                              <Input
                                id="vr-name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                aria-invalid={!!errors.name}
                                className="mt-1"
                                placeholder={t.name_placeholder}
                              />
                              <FieldError message={errors.name} />
                            </div>
                            <div>
                              <label htmlFor="vr-email">{t.email_label}</label>
                              <Input
                                id="vr-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                aria-invalid={!!errors.email}
                                className="mt-1"
                                placeholder={t.email_placeholder}
                              />
                              <FieldError message={errors.email} />
                            </div>
                          </div>

                          <div>
                            <label htmlFor="vr-phone">
                              {t.phone_label}{" "}
                              <span className="font-normal text-ink-muted">{props.translations.common.optional}</span>
                            </label>
                            <Input
                              id="vr-phone"
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              className="mt-1"
                              placeholder={t.phone_placeholder}
                            />
                          </div>

                          <div>
                            <label htmlFor="vr-modality">{t.modality_label}</label>
                            <Input
                              id="vr-modality"
                              type="text"
                              required
                              value={modality}
                              onChange={(e) => setModality(e.target.value)}
                              aria-invalid={!!errors.modality}
                              className="mt-1"
                              placeholder={t.modality_placeholder}
                            />
                            <FieldError message={errors.modality} />
                          </div>

                          <div>
                            <label htmlFor="vr-location">{t.location_label}</label>
                            <Select
                              id="vr-location"
                              required
                              value={locationPref}
                              onChange={(e) => setLocationPref(e.target.value)}
                              aria-invalid={!!errors.location_pref}
                              className="mt-1"
                            >
                              <option value="">{t.select_location}</option>
                              <option value="prestwich">Prestwich</option>
                              <option value="hebden_bridge">Hebden Bridge</option>
                              <option value="both">{t.both_locations}</option>
                            </Select>
                            <FieldError message={errors.location_pref} />
                          </div>

                          <div>
                            <fieldset>
                              <legend>{t.preferred_days}</legend>
                              <div className="mt-2 flex flex-wrap gap-4">
                                {DAYS.map((day) => (
                                  <label key={day} className="flex items-center gap-2 font-normal text-ink-body">
                                    <Checkbox checked={preferredDays.includes(day)} onChange={() => toggleDay(day)} />
                                    {t.days[day]}
                                  </label>
                                ))}
                              </div>
                            </fieldset>
                            <FieldError message={errors.preferred_days} />
                          </div>

                          <div>
                            <label htmlFor="vr-notes">
                              {t.notes_label}{" "}
                              <span className="font-normal text-ink-muted">{props.translations.common.optional}</span>
                            </label>
                            <Textarea
                              id="vr-notes"
                              rows={4}
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              className="mt-1 w-full"
                              placeholder={t.notes_placeholder}
                            />
                          </div>

                          <div className="pt-2">
                            <Button type="submit" variant="primary" size="lg" disabled={processing} className="w-full sm:w-auto">
                              {t.submit}
                            </Button>
                          </div>
                        </form>
                      </>
                    )}
                  </div>
                </section>
        ),
      },
      {
        id: "rent-a-room-therapy-link",
        present: !sectionHidden(content.therapy_link),
        render: (i) => (
                <section className={`${bandBg(i + 1)} py-16`}>
                  <div className="mx-auto max-w-3xl px-6 lg:px-8">
                    {/* The resident-therapist card, as on the Hebden page: her
                        portrait and name, then the venue's own words for the
                        visitor who wants therapy rather than a room. It sits
                        on the card's chosen photograph under the navy film —
                        the page heroes' treatment — or on plain navy. */}
                    <div className="relative grid grid-cols-1 gap-6 overflow-hidden rounded-2xl bg-teal p-8 text-white sm:grid-cols-[auto_1fr] sm:gap-8 lg:p-10">
                      <HeroFilm photo={content.therapy_link?.photo} />
                      {therapist?.photo_url && (
                        <img
                          src={therapist.photo_url}
                          alt={therapist.name}
                          className="relative h-40 w-40 rounded-2xl object-cover ring-1 ring-white/25"
                        />
                      )}
                      <div className="relative">
                        {therapist && (
                          <>
                            <p className="mb-1 text-xs font-medium uppercase tracking-widest text-white/50">
                              {tc.resident_therapist}
                            </p>
                            <p className="font-display text-2xl text-white">{therapist.name}</p>
                          </>
                        )}
                        <h2 className="mt-3 text-white">{t.therapy_heading}</h2>
                        <p className="mt-4 text-white/75">{t.therapy_body}</p>
                        {/* Her own site, not the venue's redirect to it. */}
                        <a
                          href={brand_urls.gisela_therapy}
                          className="mt-6 inline-block rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                        >
                          {t.therapy_cta}
                        </a>
                      </div>
                    </div>
                  </div>
                </section>
        ),
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={
            meta?.description ??
            "Professional therapy rooms to rent in Prestwich and Hebden Bridge — free parking, sound masking, step-free access."
          }
        />
        {/* Social previews want an absolute URL; the slide's is a path. */}
        {hero.slides[0]?.url && <meta property="og:image" content={absoluteUrl(hero.slides[0].social_url ?? hero.slides[0].url, brand_urls.sanctuary)} />}
        {hero.slides[0]?.url && <meta name="twitter:card" content="summary_large_image" />}
        {hero.slides[0]?.url && (
          <link rel="preload" as="image" href={hero.slides[0].url} fetchPriority="high" />
        )}
      </Head>
      <PublicLayout>
        {/* ── Hero ───────────────────────────────────────────────────── */}
        {!sectionHidden(content.hero) && (
          <section className="relative overflow-hidden bg-teal pt-32 pb-16 text-white">
            {/* A placed photo with no description of its own still says what
                it is a picture of. */}
            <HeroFilmSlides slides={hero.slides.map((s) => ({ ...s, alt_text: s.alt_text || t.hero_alt }))} intervalMs={hero.interval_seconds * 1000} film={hero.film} />
            <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-8">
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
                {sanctuary_locations.map((l) => l.label).join(" · ")}
              </p>
              <h1 className="text-white">{content.hero.heading}</h1>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75">
                {content.hero.subheading}
              </p>
              {/* One thing to do here. The rest of the page — what's included,
                  the rates, the locations, therapy with Gisela — is a scroll
                  away, and four pills stacked on a phone buried all of it. */}
              <div className="mt-8">
                <a
                  href="#book-a-viewing"
                  className="inline-block rounded-full bg-white px-7 py-3 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                >
                  {tc.book_a_viewing}
                </a>
              </div>
            </div>
          </section>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}
      </PublicLayout>
    </>
  )
}
